// apps/web/src/pages/auth/ResetPassword.tsx
//
// Plan 04-12b Task 1 — complete password reset.
//
// FIELDS:
//   - new_password (required, min 8)
//   - confirm_password (required, must match)
//   - mfa_code (conditional — shown when server returns MFA_REQUIRED)
//
// FLOW (two-step on the wire; one-form to the user):
//   1. Submit new_password
//   2. If server returns 401 MFA_REQUIRED → show mfa_code field + resubmit
//   3. On success → redirect to /auth/sign-in with a toast/notice
//
// CONTEXT D-13: password reset does NOT bypass MFA. If user.twoFactorEnabled,
// the server requires a TOTP code before the password rotates.

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";

import { api, ApiError } from "@/lib/api-client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordField } from "@/components/forms/EmailPasswordFields";
import { PasswordStrengthIndicator } from "@/components/forms/PasswordStrengthIndicator";

const resetPasswordSchema = z
  .object({
    newPassword: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
    mfaCode: z.string().optional(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords do not match",
  });

type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;

export function ResetPassword(): React.ReactElement {
  const params = useParams<{ token?: string }>();
  const navigate = useNavigate();
  const token = params.token ?? "";

  const [mfaRequired, setMfaRequired] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [linkInvalid, setLinkInvalid] = useState(token === "");

  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
      mfaCode: "",
    },
    mode: "onSubmit",
  });

  const passwordValue = useWatch({
    control: form.control,
    name: "newPassword",
  }) as string | undefined;

  async function onSubmit(values: ResetPasswordValues): Promise<void> {
    setServerError(null);
    if (token === "") {
      setLinkInvalid(true);
      return;
    }
    try {
      type CompleteResponse = { ok: true };
      await api<CompleteResponse>("/api/auth/reset-password/complete", {
        method: "POST",
        body: JSON.stringify({
          token,
          new_password: values.newPassword,
          mfa_code:
            values.mfaCode !== undefined && values.mfaCode.length > 0
              ? values.mfaCode
              : undefined,
        }),
      });
      // Success — redirect to sign-in with a success notice in state.
      navigate("/auth/sign-in", {
        state: { resetSuccess: true },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        type ErrBody = { error?: { code?: string; message?: string } };
        const body = err.body as ErrBody | null;
        const code = body?.error?.code;
        if (code === "MFA_REQUIRED") {
          setMfaRequired(true);
          setServerError("Enter your authenticator code to complete the reset.");
          return;
        }
        if (code === "INVALID_MFA") {
          setMfaRequired(true);
          setServerError("That code didn't match. Try again.");
          return;
        }
        if (code === "TOKEN_EXPIRED" || code === "TOKEN_CONSUMED") {
          setLinkInvalid(true);
          return;
        }
        setServerError(body?.error?.message ?? err.message);
        return;
      }
      setServerError(
        err instanceof Error ? err.message : "Could not reset password",
      );
    }
  }

  if (linkInvalid) {
    return (
      <main className="mx-auto max-w-md p-8">
        <Card>
          <CardHeader>
            <CardTitle>Reset link invalid</CardTitle>
            <CardDescription>
              The reset link is missing, expired, or already used.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link
              to="/auth/forgot-password"
              className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Request a new link
            </Link>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-md p-8">
      <Card>
        <CardHeader>
          <CardTitle>Choose a new password</CardTitle>
          <CardDescription>
            Set your new password. You'll need to sign in with it after.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
              noValidate
            >
              {serverError !== null ? (
                <Alert variant={mfaRequired ? "default" : "destructive"}>
                  <AlertTitle>
                    {mfaRequired ? "Two-factor required" : "Reset failed"}
                  </AlertTitle>
                  <AlertDescription>{serverError}</AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-2">
                <PasswordField
                  control={form.control}
                  name="newPassword"
                  label="New password"
                  autoComplete="new-password"
                  disabled={form.formState.isSubmitting}
                />
                <PasswordStrengthIndicator password={passwordValue ?? ""} />
              </div>

              <PasswordField
                control={form.control}
                name="confirmPassword"
                label="Confirm new password"
                autoComplete="new-password"
                disabled={form.formState.isSubmitting}
              />

              {mfaRequired ? (
                <FormField
                  control={form.control}
                  name="mfaCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Authenticator code</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          placeholder="123456"
                          disabled={form.formState.isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Enter the 6-digit code from your authenticator app.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}

              <Button
                type="submit"
                className="w-full"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? "Saving…" : "Reset password"}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            <Link to="/auth/sign-in" className="font-medium underline">
              Back to sign in
            </Link>
          </p>
        </CardFooter>
      </Card>
    </main>
  );
}
